import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import { buildLoanReturnNavigateState } from '../../utils/loanReceivingProfiles';
import { sanitizeUnitDetailsForApiSubmit } from '../../utils/inventoryItemForm';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import ReceivingItemsGrid, { createEmptyReceivingItem } from './ReceivingItemsGrid';

const emptyLine = () => ({
  ...createEmptyReceivingItem(),
  receivedQuantity: 1
});

const LoanOutModule = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStoreExecutive = user?.role === ROLES.STORE_EXECUTIVE;
  const canSubmitLoanOut = [
    ROLES.STORE_INSPECTOR,
    ROLES.STORE_EXECUTIVE,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ].includes(user?.role);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [items, setItems] = useState([emptyLine()]);
  const [form, setForm] = useState({
    counterpartyCompany: '',
    counterpartyContactName: '',
    counterpartyContactPhone: '',
    loanReferenceNumber: '',
    deliveryNote: '',
    returnDueDate: ''
  });
  const loadInventory = useCallback(async () => {
    try {
      const res = await axios.get('/api/store/inventory', { params: { status: 'IN_INVENTORY' } });
      setInventory(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      showToast('Failed to load inventory', 'error');
    }
  }, [showToast]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get('/api/store/loan/issuances', {
        params: { loanDirection: 'LOAN_OUT' }
      });
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      showToast('Failed to load loan-out history', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    loadInventory();
    loadHistory();
  }, [loadInventory, loadHistory]);

  const submitLoanOut = async () => {
    if (!form.counterpartyCompany.trim()) {
      showToast('Recipient company is required', 'error');
      return;
    }
    if (!items.length) {
      showToast('Add at least one item', 'error');
      return;
    }
    const issueItems = items.map((line) => {
      const fromPicker = Array.isArray(line.serialNumbers)
        ? line.serialNumbers.map((serial) => String(serial || '').trim()).filter(Boolean)
        : [];
      const fromUnits = (Array.isArray(line.unitDetails) ? line.unitDetails : [])
        .map((unit) => String(unit.serialNumber || '').trim())
        .filter(Boolean);
      const serialNumbers = fromPicker.length > 0
        ? fromPicker
        : fromUnits;
      return {
        inventoryItemId: line.inventoryItemId,
        quantity: Number(line.receivedQuantity) || Number(line.quantity) || 0,
        storeType: line.storeType,
        locationText: line.locationText,
        unitDetails: sanitizeUnitDetailsForApiSubmit(line.unitDetails),
        serialNumbers
      };
    });
    const invalid = issueItems.find((line) => !line.inventoryItemId || !line.quantity || line.quantity < 1);
    if (invalid) {
      showToast('Each line needs an inventory item and issue quantity', 'error');
      return;
    }
    try {
      setLoading(true);
      await axios.post('/api/store/loan/issue', {
        issuedTo: user?._id,
        sourceInventoryDomain: 'STORE',
        counterpartyCompany: form.counterpartyCompany,
        counterpartyContactName: form.counterpartyContactName,
        counterpartyContactPhone: form.counterpartyContactPhone,
        loanReferenceNumber: form.loanReferenceNumber,
        deliveryNote: form.deliveryNote,
        returnDueDate: form.returnDueDate ? new Date(form.returnDueDate).toISOString() : undefined,
        items: issueItems
      });
      showToast(
        isStoreExecutive
          ? 'Loan-out submitted for Store Manager approval'
          : 'Loan-out submitted for AM approval',
        'success'
      );
      setForm({
        counterpartyCompany: '',
        counterpartyContactName: '',
        counterpartyContactPhone: '',
        loanReferenceNumber: '',
        deliveryNote: '',
        returnDueDate: ''
      });
      setItems([emptyLine()]);
      loadHistory();
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to submit loan-out'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const startLoanOutReturnReceiving = async (record) => {
    let loanInRecords = [];
    try {
      const res = await axios.get('/api/store/receiving', {
        params: { loanDirection: 'LOAN_IN', workflowStatus: 'APPROVED', loanBorrowOnly: 'true' }
      });
      loanInRecords = Array.isArray(res.data) ? res.data : [];
    } catch {
      loanInRecords = [];
    }
    const navState = buildLoanReturnNavigateState(record, 'LOAN_OUT_RETURN', { loanInRecords });
    if (!navState.loanReceivingPrefill?.items?.length) {
      showToast('No outstanding quantities to return on this loan-out', 'info');
      return;
    }
    navigate('/receiving-and-inspection', { state: navState });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Loan Out (Give Loan)</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Loan-out uses owned store inventory. Store Executive submissions require Store Manager approval before AM approval.
        Returns are received into store via Receiving and Inspection → Loan/Rental.
      </Alert>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Recipient Company" value={form.counterpartyCompany} onChange={(e) => setForm((p) => ({ ...p, counterpartyCompany: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Recipient Contact Name" value={form.counterpartyContactName} onChange={(e) => setForm((p) => ({ ...p, counterpartyContactName: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Recipient Contact Phone" value={form.counterpartyContactPhone} onChange={(e) => setForm((p) => ({ ...p, counterpartyContactPhone: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Loan Reference Number" value={form.loanReferenceNumber} onChange={(e) => setForm((p) => ({ ...p, loanReferenceNumber: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth type="date" InputLabelProps={{ shrink: true }} label="Return Due Date" value={form.returnDueDate} onChange={(e) => setForm((p) => ({ ...p, returnDueDate: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Issue Note" value={form.deliveryNote} onChange={(e) => setForm((p) => ({ ...p, deliveryNote: e.target.value }))} />
          </Grid>
        </Grid>
        <ReceivingItemsGrid
          items={items}
          onItemsChange={setItems}
          inventoryOptions={inventory}
          issueMode
          showCertificateUpload={false}
          emptyMessage="Add items from store inventory to loan out"
        />
        {!canSubmitLoanOut && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Only Store Inspector, Store Executive, Accountable Manager, or Super Admin can submit loan-out.
          </Alert>
        )}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={submitLoanOut}
            disabled={loading || !canSubmitLoanOut}
          >
            {loading ? 'Submitting...' : 'Submit for AM Approval'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Loan-Out History</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Return Due</TableCell>
              <TableCell>Outstanding</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((row) => (
              <TableRow key={row._id}>
                <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}</TableCell>
                <TableCell>{row.counterpartyCompany || '—'}</TableCell>
                <TableCell>{row.loanReferenceNumber || '—'}</TableCell>
                <TableCell>
                  {row.returnDueDate ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{new Date(row.returnDueDate).toLocaleDateString()}</Typography>
                      {new Date(row.returnDueDate) < new Date() && row.status !== 'RETURNED' && (
                        <Chip size="small" color="error" label="Overdue" />
                      )}
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  {(Array.isArray(row.items) ? row.items : []).reduce((sum, line) => {
                    const issued = Number(line.quantity || 0);
                    const returned = Number(line.quantityReturned || 0);
                    return sum + Math.max(0, issued - returned);
                  }, 0)}
                </TableCell>
                <TableCell>{row.approvalStatus || '—'}</TableCell>
                <TableCell>{row.status || '—'}</TableCell>
                <TableCell>
                  {['ISSUED', 'PARTIAL_RETURN'].includes(String(row.status || '')) ? (
                    <Button size="small" variant="outlined" onClick={() => startLoanOutReturnReceiving(row)}>
                      Receive return in Store
                    </Button>
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

    </Box>
  );
};

export default LoanOutModule;
